import { useCallback, useContext, useMemo, useState } from 'react';
import { ModalBody, ModalFooter } from 'reactstrap';
import { OkButton } from 'src/components/Buttons/OkButton';
import {
  EventSearch,
  OnEventsSelectCallback,
} from 'src/components/Events/EventSearch';
import { isCampaignActive } from 'src/components/SponsoredListings/SponsoredListings.util';
import { useAdCampaignsDataContext } from 'src/contexts/AdCampaignsDataContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import {
  BulkBoostModal,
  useBulkBoostActions,
} from 'src/dialogs/BulkEdits/BulkBoostEvents';
import { CancellableFormFooter } from 'src/modals/common/CancellableFormFooter';
import { CancellableFormHeader } from 'src/modals/common/CancellableFormHeader';
import { EventEntityHeader } from 'src/modals/common/EventEntityHeader';
import { ModalBodyDataContainer } from 'src/modals/Modal/Modal.styled';
import { CampaignModal } from 'src/modals/SponsoredListings/CampaignModal';
import { ContentId } from 'src/utils/constants/contentId';
import { Event } from 'src/WebApiController';

import {
  ActiveBoostDisplay,
  BoostEventSearchCatalogProvider,
} from './BoostEventSearchModal.components';
import {
  useBoostIngressEntityCatalog,
  useBoostIngressScopedEvents,
} from './BoostEventSearchModal.hooks';
import { BoostEventSelection } from './BoostEventSearchModal.types';
import {
  isEventBoostEligible,
  pickCampaignForEvent,
} from './BoostEventSearchModal.utils';

/**
 * Campaign manager Boost ingress: event search step.
 *
 * Reuses the purchase-creation event search UI, scoped so that only events
 * with active inventory for this account can be displayed or selected.
 * Continuing with a single event that has an active boost opens the edit
 * surface for that campaign (today's active flight + tomorrow's flight
 * budget); a single eligible unboosted event opens the regular campaign
 * creation surface (CampaignModal); anything else routes to the bulk boost
 * creation surface (BulkBoostModal).
 */
export const BoostEventSearchModal = () => {
  const { setModal } = useContext(ModalContext);
  const { allCampaignData } = useAdCampaignsDataContext();
  const { appContext } = useAppContext();
  const { data: catalogData } = useCatalogDataContext();
  const { scopedEvents, isLoading } = useBoostIngressScopedEvents();
  const entityCatalog = useBoostIngressEntityCatalog(scopedEvents);

  const [selectedEvents, setSelectedEvents] = useState<BoostEventSelection>(
    () => new Map()
  );

  const selectedVirtIds = useMemo(
    () => [...selectedEvents.keys()],
    [selectedEvents]
  );
  const selectedEventsList = useMemo(
    () => [...selectedEvents.values()],
    [selectedEvents]
  );

  const {
    unboostedCount,
    pausedCount,
    activeCount,
    ineligibleCount,
    unboostedEvents,
    pausedEvents,
    activeEvents,
    ineligibleEvents,
    executeBulkBoost,
  } = useBulkBoostActions(selectedVirtIds);

  const onEventsSelect = useCallback<OnEventsSelectCallback>(
    (data) => {
      setSelectedEvents((prev) => {
        const next = new Map(prev);
        data.forEach(({ event, isSelected }) => {
          if (isSelected) {
            const eventData = scopedEvents?.find(
              (e) => e.event.virtId === event.virtId
            );
            if (eventData) {
              next.set(event.virtId, eventData);
            }
          } else {
            next.delete(event.virtId);
          }
        });
        return next;
      });
    },
    [scopedEvents]
  );

  const onDeleteNumEventsSelected = useCallback(() => {
    setSelectedEvents(() => new Map());
  }, []);

  // Surfaced on result cards whose event already has an active boost —
  // regardless of selection state, so the existing campaign is visible
  // while scanning results: the campaign's spend bar, remaining budget,
  // and boost icon, right-aligned in the card (see ActiveBoostDisplay)
  const renderEventItemTrailing = useCallback(
    (event: Event) => {
      if (event.viagId == null) {
        return null;
      }
      const campaign = pickCampaignForEvent(allCampaignData, event.viagId);
      if (!campaign || !isCampaignActive(campaign)) {
        return null;
      }
      return <ActiveBoostDisplay campaign={campaign} />;
    },
    [allCampaignData]
  );

  const isSearchReady = !isLoading && scopedEvents != null;

  const onContinue = useCallback(() => {
    if (selectedEventsList.length === 0) {
      return;
    }

    // A single selection on an event with an active boost edits that
    // campaign directly — the edit surface shows today's active flight and
    // tomorrow's flight budget. This takes priority over the create-flow
    // eligibility check, which gates new campaign creation, not editing an
    // existing one.
    if (selectedEventsList.length === 1) {
      const eventData = selectedEventsList[0];
      const viagId = eventData.event.viagId;
      const activeCampaign =
        viagId != null ? pickCampaignForEvent(allCampaignData, viagId) : null;
      if (
        viagId != null &&
        activeCampaign &&
        isCampaignActive(activeCampaign)
      ) {
        setModal({
          children: (
            <CampaignModal
              eventId={viagId}
              campaign={activeCampaign}
              requireCancelConfirmation
            />
          ),
          clickOutsideToClose: true,
          size: 'slide-in-md',
        });
        return;
      }

      // A single eligible selection without an active boost opens the
      // regular campaign creation surface
      const isEligible = isEventBoostEligible(
        eventData,
        catalogData,
        appContext?.sponsoredListingsAllowedCountryCodes
      );
      if (isEligible) {
        setModal({
          children: (
            <CampaignModal
              eventId={viagId!}
              campaign={activeCampaign}
              requireCancelConfirmation
            />
          ),
          clickOutsideToClose: true,
          size: 'slide-in-md',
        });
        return;
      }
    }

    // Multiple selections (or a single ineligible one) use the bulk boost
    // creation surface
    setModal({
      children: (
        <BulkBoostModal
          unboostedCount={unboostedCount}
          pausedCount={pausedCount}
          activeCount={activeCount}
          ineligibleCount={ineligibleCount}
          unboostedEvents={unboostedEvents}
          pausedEvents={pausedEvents}
          activeEvents={activeEvents}
          ineligibleEvents={ineligibleEvents}
          onExecute={executeBulkBoost}
          requireCancelConfirmation
        />
      ),
      clickOutsideToClose: true,
      size: 'slide-in-md',
    });
  }, [
    activeCount,
    activeEvents,
    allCampaignData,
    appContext,
    catalogData,
    executeBulkBoost,
    ineligibleCount,
    ineligibleEvents,
    pausedCount,
    pausedEvents,
    selectedEventsList,
    setModal,
    unboostedCount,
    unboostedEvents,
  ]);

  // Progress to lose when exiting the search step: any picked events
  const hasSelectedEvents = selectedEventsList.length > 0;

  return (
    <>
      <CancellableFormHeader showDialogOnCancel={hasSelectedEvents}>
        <EventEntityHeader title={<Content id={ContentId.BoostEvents} />} />
      </CancellableFormHeader>
      <ModalBody>
        <ModalBodyDataContainer>
          {isSearchReady ? (
            <BoostEventSearchCatalogProvider
              performers={entityCatalog?.performers}
              venues={entityCatalog?.venues}
            >
              <EventSearch
                onEventsSelect={onEventsSelect}
                selectedEvents={selectedVirtIds}
                eventsToSearchFrom={scopedEvents}
                deriveScopedEntityResults
                catalogData={catalogData}
                disableParkingTicketsFilter
                alwaysShowDropdownSections
                numEventsSelected={selectedEventsList.length}
                onDeleteNumEventsSelected={onDeleteNumEventsSelected}
                renderEventItemTrailing={renderEventItemTrailing}
              />
            </BoostEventSearchCatalogProvider>
          ) : (
            <PosSpinner />
          )}
        </ModalBodyDataContainer>
      </ModalBody>
      <ModalFooter>
        <CancellableFormFooter showDialogOnCancel={hasSelectedEvents}>
          <OkButton
            data-testid="boost-ingress-continue-button"
            variant="regular"
            shape="pill"
            onClick={onContinue}
            disabled={!isSearchReady || selectedEventsList.length === 0}
            textContentId={ContentId.Next}
          />
        </CancellableFormFooter>
      </ModalFooter>
    </>
  );
};
